import React, {useEffect, useState, useContext, useRef} from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = React.createContext();

export const useAuth = () => {
    return  useContext(AuthContext);
}

export const AuthProvider = ( {children} ) => {
    const [currentUser, setCurrentUser] = useState();
    const [isAdmin, setIsAdmin] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    // Holds the live-listener teardown function so we can detach when the
    // user logs out or auth state changes to a different user.
    const unsubscribeUserDocRef = useRef(null);

    const signUp = async (email, password) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // Create user document in Firestore with default profile. Use the
        // normalized email from the Auth user (not the raw form value) so it
        // matches request.auth.token.email in the Firestore create rule.
        await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: userCredential.user.email,
            role: 'user',
            createdAt: new Date().toISOString(),
            displayName: null,
            phone: null,
            phoneVerified: false,
            smsPreferences: { newGames: false, kickoffReminder: false, leaderboardUpdate: false },
        });
        return userCredential;
    }

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    }

    const logout = () => {
        setIsAdmin(false);
        setUserProfile(null);
        if (unsubscribeUserDocRef.current) {
            unsubscribeUserDocRef.current();
            unsubscribeUserDocRef.current = null;
        }
        return signOut(auth);
    }

    const resetPassword = (email) => {
        return sendPasswordResetEmail(auth, email);
    }

    const updateProfile = async (fields) => {
        if (!currentUser) return;
        const userDocRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userDocRef, fields);
        setUserProfile(prev => ({ ...prev, ...fields }));
    };

    // Check admin status from Firestore user document and subscribe to live
    // updates so server-side writes (verifyPhoneCode → phoneVerified=true,
    // smsInboundWebhook STOP → smsOptOut=true, etc.) propagate to the UI
    // without a page refresh.
    //
    // This path only READS. The user doc is created exactly once, by signUp —
    // a second creator here would race signUp's setDoc (each computes its own
    // createdAt, so the loser becomes an update touching a restricted field
    // and gets denied by rules). If the doc doesn't exist yet, we fall back
    // to an in-memory default profile and let the onSnapshot listener pick up
    // the doc when signUp's write lands.
    const checkAdminStatus = async (user) => {
        // Tear down any prior listener before starting a new one (or skipping)
        if (unsubscribeUserDocRef.current) {
            unsubscribeUserDocRef.current();
            unsubscribeUserDocRef.current = null;
        }
        if (!user) {
            setIsAdmin(false);
            return;
        }
        try {
            // Ensure the auth token is fully propagated to Firestore
            // before making any reads (prevents "client is offline" errors
            // when restrictive Firestore rules are deployed)
            await user.getIdToken();
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
                // Doc not created yet (signup write still in flight) or missing
                // entirely. Use an in-memory default so the UI isn't stuck on a
                // null profile — no client write; signUp is the single creator.
                setIsAdmin(false);
                setUserProfile({
                    displayName: null,
                    phone: null,
                    phoneNumber: null,
                    phoneVerified: false,
                    smsOptOut: false,
                    smsPreferences: { newGames: false, kickoffReminder: false, leaderboardUpdate: false },
                });
            }

            // Subscribe to live updates. Fires once immediately with current
            // state, then again on every subsequent write to the doc.
            unsubscribeUserDocRef.current = onSnapshot(
                userDocRef,
                (snap) => {
                    if (!snap.exists()) return;
                    const data = snap.data();
                    setIsAdmin(data.role === 'admin');
                    setUserProfile({
                        displayName: data.displayName || null,
                        phone: data.phone || null,
                        phoneNumber: data.phoneNumber || null,
                        phoneVerified: data.phoneVerified || false,
                        smsOptOut: data.smsOptOut || false,
                        smsPreferences: data.smsPreferences || { newGames: false, kickoffReminder: false, leaderboardUpdate: false },
                    });
                },
                (error) => {
                    console.error('User doc listener error:', error);
                }
            );
        } catch (error) {
            console.error('Error subscribing to user doc:', error);
            setIsAdmin(false);
        }
    };

    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
            setCurrentUser(user);
            await checkAdminStatus(user);
            setLoading(false);
        })

        return () => {
            unsubscribeAuth();
            if (unsubscribeUserDocRef.current) {
                unsubscribeUserDocRef.current();
                unsubscribeUserDocRef.current = null;
            }
        };
    }, []);

    const value = {
        currentUser,
        isAdmin,
        userProfile,
        updateProfile,
        signUp,
        login,
        logout,
        resetPassword
    }

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    )
}
